import { Injectable }   from '@angular/core';
import { FormItem }     from "./infinity-item/interfaces/FormItem.interface";
import { FormRule }     from "./infinity-engine/interfaces/FormRule.interface";

@Injectable({ providedIn: 'root' })
export class InfinityUtil {
    public store: any = {};             // Stores values.
    public listeners: any = {};         // Stores listener arrays.
    public onceListeners: any = {};     // Stores listener arrays that only run once.
    public timeouts: any = {};          // Stores timeouts.
    
    constructor() {
        this.bindAll(this); let w: any = window; w.util = this;                 // Bind "this" value to functions and allow access from window.
        this.watch("scroll", (value: number) => this.scroll(value, ".main"));   // Scroll to passed value.
    }

    /* ----- Utilities ----- */

    /**
     * [Impure] Bind all methods to their parent object.
     * @param obj - "this" object to bind all functions to.
     */
    bindAll(obj: any) {
        this.getMethodNames(obj).forEach(key => obj[key] = obj[key].bind(obj));  // Bind each method to the parent object.
    }

    /**
     * [Pure] Compare two values using string comparator.
     * @param initialValue - First value to compare.
     * @param comparator - Comparator string (ex. '==', '>').
     * @param compareValue - Second value to compare.
     */
    compare(initialValue: any, comparator: string, compareValue?: any): boolean {
        // Turn currency into numbers.
        if (typeof initialValue === "string" && initialValue[0] === "$") initialValue = parseInt(initialValue.replace(/[$,]/g, ""));
        if (typeof compareValue === "string" && compareValue[0] === "$") compareValue = parseInt(compareValue.replace(/[$,]/g, ""));
        
        if (comparator === "!") return [null, "", undefined, false].indexOf(initialValue) > -1;
        if (comparator === "!!") return [null, "", undefined, false].indexOf(initialValue) < 0;
        if (comparator === "==") return initialValue == compareValue;
        if (comparator === "!=") return initialValue != compareValue;
        if (comparator === "<") return initialValue < compareValue;
        if (comparator === ">") return initialValue > compareValue;

        if (comparator === "regex") {
            if (!initialValue) return false;
            if (typeof initialValue === "number") initialValue = initialValue.toString();
            return initialValue.match(new RegExp(compareValue));
        }
        return false;
    }

    /**
     * [Pure] Compare two date values using string comparator.
     * @param initialValue - First date to compare.
     * @param comparator - Comparator string.
     * @param compareValue - Second date to compare (can be semantic).
     */
    compareDate(initialValue: string | Date, comparator: string, compareValue?: any): boolean {
        // Get date for initial value.
        let dateValue;
        if (typeof initialValue === "string") dateValue = new Date(initialValue);
        if (initialValue instanceof Date) dateValue = initialValue;

        // Get date for compare value.
        if (compareValue === "today") compareValue = new Date();
        else if (typeof compareValue === "string") compareValue = new Date(compareValue);

        // Compare dates.
        if (comparator === "<") return dateValue < compareValue;
        if (comparator === ">") return dateValue > compareValue;
        if (comparator === "!!") {
            if (initialValue && initialValue instanceof Date) return true;
            if (typeof initialValue === "string") {
                const dateValues = initialValue.split("/");
                if (dateValues.length === 2) return dateValues[0].length === 2 && dateValues[1].length === 2;                               // -> 09/21
                if (dateValues.length === 3) return dateValues[0].length === 2 && dateValues[1].length === 2 && dateValues[2].length === 4; // -> 09/21/1990
            }
        }
        return false;
    }

    /**
     * [Impure] Copy property values from one object into another and return the result.
     * @param toObj - Object to copy properties in to.
     * @param fromObj - Object to copy properties from.
     * @param config - Optional configuration properties { onlyExisting, notExisting, newObject }.
     */
    copy(toObj: any, fromObj: any, config?: any): any {
        if (!config) config = {};
        const { notExisting, onlyExisting, newObject } = config;                    // Alias config properties.
        if (newObject) toObj = this.copy({}, toObj);                                // If passed "newObject", create new object copy of "to" object.
        Object.keys(fromObj).forEach(key => {                                       // Iterate over all keys on "from" obj.
            if (notExisting && toObj[key] !== undefined) return;                    // If passed "notExisting", only copy properties "to" object doesn't have.
            if (onlyExisting && toObj[key] === undefined) return;                   // If passed "onlyExisting", only copy properties "to" object already has.
            if (toObj[key] instanceof Object) this.copy(toObj[key], fromObj[key]);  // If "to" obj value at that key is an object, recurse on that object.
            else toObj[key] = fromObj[key];                                         // Otherwise set "to" obj value equal to "from" obj value.
        });
        return toObj;                                                               // Return "to" object.
    }

    /**
     * [Impure] Set value in nested object structure.
     * @param obj - Root object.
     * @param key - "." separated key string.
     * @param value - Value to be set.
     */
    deepSet(obj: any, key: string, value: any) {
        const keySplit = key.split(".");
        let target = obj;
        keySplit.forEach((prop, i) => {
            if (i === key.split.length - 1) return target[prop] = value;
            if (target[prop] === undefined) target[prop] = {};
            target = target[prop];
        });
    }

    /**
     * [Pure] Compare deep equality of two objects.
     * @param obj1 - First object to compare.
     * @param obj2 - Second object to compare.
     */
    equals(obj1: any, obj2: any): boolean {
        if (obj1 === obj2) return true;                                     // Return true if values match.
        if (typeof obj1 !== typeof obj2) return false;                      // Exit if different types.
        if (Array.isArray(obj1)) return obj1.reduce((result, e, i) => {     // If obj1 is an array:
            return !result ? false : this.equals(e, obj2[i]);               // Compare each array index against obj2.
        }, true);
        if (!(obj1 instanceof Object)) return false;                        // Exit if not an object (means it's a value and the values don't match).
        const obj1Keys = Object.keys(obj1);                                 // Get keys from obj1.
        const obj2Keys = Object.keys(obj2);                                 // Get keys from obj2.
        if (obj1Keys.length !== obj2Keys.length) return false;              // Exit if different number of keys.
        for (let i = 0; i < obj1Keys.length; i++) {                         // Loop over object keys.
            if (obj2Keys.indexOf(obj1Keys[i]) === -1) return false;         // Exit if key doesn't exist in both objects.
            let val1 = obj1[obj1Keys[i]];                                   // Get value from obj1.
            let val2 = obj2[obj1Keys[i]];                                   // Get value from obj2.
            if (!this.equals(val1, val2)) return false;                     // Exit if values don't match.
        }
        return true;                                                        // Return true if nothing else exittedq.
    }

    /**
     * [Pure] Return subset of an array of objects where all properties match the passed object.
     * @param arr - Array of objects to filter.
     * @param obj - Object to match properties against.
     */
    filter(arr: Array<any>, obj: any): any {
        return (arr || []).filter(e => {                                        // Filter array by matching properties to object.
            for (let i = 0, keys = Object.keys(obj); i < keys.length; i++) {    // Iterate over object keys.
                if (e[keys[i]] !== obj[keys[i]]) return false;                  // If any key doesn't match, return false.
            }
            return true;                                                        // If you make it through the loop, all keys must have matched.
        });
    }

    /**
     * [Pure] Get method names from the prototype of an object / class.
     * @param obj - Object / class to get methods from.
     */
    getMethodNames(obj: any): Array<string> {
        const proto = Object.getPrototypeOf(obj);                                           // Get prototype of object / class.
        const props = Object.getOwnPropertyNames(proto);                                    // Get properties from prototype.
        return props.filter(key => obj[key] instanceof Function && key !== "constructor");  // Filter for methods.
    }

    /**
     * [Pure] Send HTTP request and return response as promise.
     * @param verb - Method to use (get, post, put).
     * @param url - Endpoint to hit.
     * @param config - Configuration object.
     */
    async httpPromise(verb: string, url: string, config?: any): Promise<any> {
        const request = new XMLHttpRequest();
        let { data } = (config || { data: null });
        return new Promise((resolve, reject) => {
            request.addEventListener("load", () => resolve(JSON.parse(request.responseText)));
            request.open(verb, url);
            if (data) request.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
            if (data && typeof data !== "string") data = JSON.stringify(data);
            request.send(data || null);
        });
    }

    isIe(): number | boolean {
        var ua = window.navigator.userAgent;

        // IE 10 or older => return version number
        var msie = ua.indexOf('MSIE ');
        if (msie > 0) return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);

        // IE 11 => return version number
        var trident = ua.indexOf('Trident/');
        if (trident > 0) {
            var rv = ua.indexOf('rv:');
            return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
        }

        // Edge => return version number
        var edge = ua.indexOf('Edge/');
        if (edge > 0) return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);

        // other browser
        return false;
    }

    /**
     * [Pure] Turn file into encodedBinary.
     * @param file - File to turn into binary.
     */
    async readFileAsync(file: any): Promise<any> {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onloadend = (e: any) => {
                const bytes = new Uint8Array(e.target.result);
                let binary = '';
                for (var j = 0; j < bytes.byteLength; j++) binary += String.fromCharCode(bytes[j]);
                resolve(btoa(binary));
            }
            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * [Impure] Scroll body or element from key.
     * @param value - Position to scroll to.
     * @param key - Key to select element with.
     */
    scroll(value: number | string, key?: string) {
        let target;
        if (typeof value === "string") value = parseInt(value);
        if (key[0] === "#") target = document.getElementById(key.slice(1));
        if (key[0] === ".") target = document.getElementsByClassName(key.slice(1))[0];
        if (target) target.scrollTop = value;
        window.scrollTo(0, value);
    }

    /**
     * Return true if value is truthy.
     * @param arr
     */
    secondArrayValueTruthy(arr: Array<any>) {
        return !!arr[1];
    }

    /**
     * Sort children of item and recurse for grandchildren.
     * @param obj
     * @param childKey
     * @param sortKey
     */
    sortAll(obj: any, childKey: string, sortKey: string) {
        if (!obj[childKey] || !(obj[childKey] instanceof Array)) return;
        obj[childKey].sort(this.sortBy([sortKey]));
        obj[childKey].forEach(child => this.sortAll(child, childKey, sortKey));
    }

    /**
     * [Impure?] Return a function that sorts an array of objects by order of given keys.
     * @param keys - Array of keys to order sort by.
     * @param isDescending - Boolean to determine sorting direction.
     */
    sortBy(keys: Array<string>, isDescending?: boolean): any {
        return function (a: any, b: any) {                                  // Return sorter function.
            for (let i = 0; i < keys.length; i++) {                         // Iterate over sort keys, exiting whenever anything matches.
                if (a[keys[i]] < b[keys[i]]) return isDescending ? 1 : -1;  // Compare values and return if matched.
                if (a[keys[i]] > b[keys[i]]) return isDescending ? -1 : 1;  // Compare values and return if matched.
            }
            return 0;                                                       // Return 0 if all values end up equal.
        };
    }

    /**
     * Cascading timeouts.
     * @param arr - Array of arrays of [timer: number, fn: Function]
     */
    timeMachine(arr: Array<Array<any>>) {
        const [timer, fn] = arr.shift();
        setTimeout(() => {
            fn();
            if (arr.length) this.timeMachine(arr);
        }, timer);
    }

    /**
     * Turns "spaced" string into camel case.
     * @param str - String to make camel case.
     */
    toCamelCase(str: string): string {
        return str[0].toLowerCase() + str.slice(1).replace(/ /g, "");
    }

    /**
     * [Pure] Turns number or string into currency string.
     * @param amount - Number or parseInt viable string to turn into currency string.
     * @param hideDecimal - Boolean to determine whether to display decimals.
     */
    toCurrency(amount: any, hideDecimal?: boolean): string {
        const negativeSign = amount < 0 ? "-" : "";
        amount = Math.abs(Number(amount) || 0).toFixed(2);
        let i: any = parseInt(amount).toString();
        let j = (i.length > 3) ? i.length % 3 : 0;
        const top = j ? i.substr(0, j) + "," : '';                                          // 1000000.50 -> 1,
        const body = i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + ",");                     // 1000000.50 -> 000,000
        const decimal = !hideDecimal ? "." + Math.abs(amount - i).toFixed(2).slice(2) : ""; // 1000000.50 -> .50
        return negativeSign + "$" + top + body + decimal;
    }

    /**
     * Return true if value is truthy.
     * @param value
     */
    truthy(value: any) {
        return !!value;
    }

    /* ----- Store ----- */

    /**
     * [Pure] Get value for key or { key: value } object for array of keys from store. 
     * @param key - Property to get from store.
     * @param store - Object to use instead of store.
     * @param fallback - Result to return if no match found.
     */
    get(key: string | Array<string> | number | any, store?: any, fallback?: any): any {
        if (!store) store = this.store;
        if (typeof key === "number") key = key.toString();
        // Get from string.
        if (typeof key === "string") {
            const keys = key.split(".");                                                    // "prop1.prop2" -> [ "prop1", "prop2" ]
            for (let i = 0; i < keys.length; i++) {                                         // Iterate over property array
                if ([null, undefined, NaN].indexOf(store[keys[i]]) > -1) return fallback;   // If no value exists at store[prop], return fallback.
                store = store[keys[i]];                                                     // Otherwise set store to store[prop] and continue iteration.
            }
            return store;
        }
        // Get from Array<string>.
        if (key instanceof Array) {
            const isNestedString = { '"': true, "'": true };
            return key.reduce((result, key) => {
                let nullable = key.slice(-1) === "?";                                   // Determine if key is nullable.
                let value = nullable ? store[key.slice(0, -1)] : store[key];            // Set initial value.
                if (key.indexOf(":") > -1) {                                            // ":" indicates assignment.
                    let split = key.split(":");                                         // "newTestProp: originalTestProp" -> [ "newTestProp", "originalTestProp" ]
                    key = split[0].trim();                                              // Initialize key as whatever is passed before the ":"
                    value = split.slice(1).join(":").trim();                            // Initialize value as whatever is passed after the ":"
                    if (value.match(/^[0-9]*$/, "g")) value = parseInt(value);          // If assigned to a number, parse the number
                    else if (value === "true") value = true;                            // If assigned to a boolean, parse the boolean
                    else if (value === "false") value = false;                          // If assigned to a boolean, parse the boolean
                    else if (isNestedString[value[0]]) value = value.slice(1, -1);      // If assigned to a string, parse the string
                    else value = this.get(value, store);                                // Otherwise, assign to value of second key
                }
                if (!nullable || value !== undefined) result[key] = value;
                return result;
            }, {});
        }
        // Get from { [prop]: any }.
        if (key instanceof Object) {
            return Object.keys(key).reduce((obj, prop) => {
                if (["number", "boolean"].indexOf(typeof key[prop]) > -1) {
                    obj[prop] = key[prop];
                }
                if (typeof key[prop] === "string") {
                    let value = this.get(key[prop], store);
                    if (value !== undefined) obj[prop] = value;
                }
                if (key[prop] instanceof Array) {
                    let values = key[prop].slice(0,-1).map(val => this.get(val, store));
                    let fn = key[prop].slice(-1)[0];
                    let result = fn.apply(null, values);
                    if (result !== undefined) obj[prop] = result;
                }
                return obj;
            }, {});
        }
        return undefined;
    }

    /**
     * [Impure] Set value in store for key or group of keys.
     * @param valueObj - Object to copy properties in to store.
     */
    set(valueObj: any, config?: any) {
        // Batch rule results.
        if (config && config.batch) {
            this.store.preset = valueObj;
            valueObj = Object.keys(valueObj).reduce(this.updateReducer, valueObj);
        }
        
        // Get keys to update.
        let keys = Object.keys(valueObj);
        if (config && config.sort) keys.sort((a, b) => a.localeCompare(b));

        // Update and emit.
        keys.forEach(key => {
            const previousValue = this.get(key);                            // Store previous value.
            if (key.indexOf(".") === -1) this.store[key] = valueObj[key];   // Set new value.
            else this.deepSet(this.store, key, valueObj[key]);              // Use deepset if key has "."
            if (!config || !config.silent) this.emit(key, previousValue);   // Run listeners if not silent.
        });

        this.store.preset = undefined;
    }

    setItemValue(key: string, value: any) {
        const item = this.get(key);
        item.value = value;
        this.set({ [key]: item }, { batch: true });
    }

    /* ----- Event Listener ----- */

    /**
     * [Impure] Run listener functions with (current value from store, previous value).
     * @param key - Key to run listeners for.
     * @param previousValue - Optional previous value.
     */
    emit(key: string, previousValue?: any) {
        (this.listeners["*"] || []).forEach(obj => obj.fn(this.get(key), previousValue, key));
        const listeners = (this.listeners[key] || []).concat(this.onceListeners[key] || []);    // Concatenate all listeners.
        listeners.sort(this.sortBy(["priority"], true));                                        // Sort listeners by priority (higher first).
        listeners.forEach(obj => obj.fn(this.get(key), previousValue, key));                    // Run listeners.
        this.onceListeners[key] = undefined;                                                    // Remove "once" listeners.
    }

    /**
     * [Impure] Add function to listeners to run when value is set.
     * @param key - Key to watch in store.
     * @param fn - Function to run when store[key] is updated.
     * @param config - Optional configuration object.
     */
    watch(key: string, fn: Function | any, config: any = {}) {
        const defaults = { once: false, backfill: false, priority: 0 };                             // Set defaults.
        const { once, backfill, priority } = this.copy(config, defaults, { notExisting: true });    // Copy defaults for non-existent properties.
        let value = this.get(key);                                                                  // Get value from store.
        if (value === undefined) value = (this.get("preset") || {})[key];
        const listeners = this[once ? "onceListeners" : "listeners"];       // Determine listener object to add function to.
        if (backfill && value !== undefined) fn(value);                     // If backfill option is true, and value exists, run function with value.
        if (backfill && value !== undefined && once) return;                // If once option is true, exit here without adding fn to listeners.
        listeners[key] = (listeners[key] || []).concat({ fn, priority });   // Push function to listener array (create array if none exists).
    }

    /* ----- Rule Engine ----- */

    /**
     * [Impure] Run rule and return value.
     * @param rule - Rule to run.
     */
    ruleEngine(rule: FormRule): any {
        if (!(rule instanceof Object)) throw new Error();
        let { compare, compareDate, any, all, concat, reduce, not, equal, fallback } = rule;
        let result: boolean;
        if (compare) result = this.compare.apply(null, compare.map(this.ruleValue));
        if (compareDate) result = this.compareDate.apply(null, compareDate.map(this.ruleValue));
        if (any) result = any.reduce((result, subrule) => result || this.ruleEngine(subrule), false);
        if (all) result = all.reduce((result, subrule) => !result ? false : this.ruleEngine(subrule), true);
        if (concat) result = concat.reduce((a, b) => a + b, "");
        if (reduce) {
            let [itemsKey, reducerKey, initialValue] = reduce;
            let items = this.get(itemsKey) || (this.get("preset") || {})[itemsKey];
            let reducer = this.get(reducerKey) || this[reducerKey];
            if (items) result = items.reduce(reducer, initialValue);
        }
        if (not) result = !this.ruleEngine(not);
        if (equal && (result || result === undefined)) result = this.ruleValue(equal);
        else if (fallback) result = this.ruleValue(fallback);
        return result;
    }

    /**
     * [Pure] Determine value to use in rule.
     * @param value - Initial value.
     */
    ruleValue(value: any): any {
        if (typeof value === "string" && value[0] === "#") {
            let presetValue = this.get(value.slice(1), this.store.preset || {});
            let storeValue = this.get(value.slice(1));
            value = presetValue !== undefined ? presetValue : storeValue;
        }
        if (typeof value === "string" && value.match(/^\d+(?:\.\d*)?$/)) value = parseInt(value);   // Parse integers.
        return value;
    }

    /**
     * Add rule to existing rules object.
     * @param rules
     * @param watchTarget
     * @param setTarget
     * @param rule
     */
    addRule(rules, watchTarget, setTarget, rule) {
        if (!rules[watchTarget]) rules[watchTarget] = {};                           // Create ruleset if none exists.
        if (!rules[watchTarget][setTarget]) rules[watchTarget][setTarget] = rule;   // Add rule to ruleset if no rule exists at setTarget.
        else {
            // Determine if rule is unique.
            const existingRule = rules[watchTarget][setTarget];                                                     // Get existing rule for setTarget.
            const allExistingRules = [existingRule].concat(existingRule.all || []).map(e => JSON.stringify(e));     // Get array of JSON rules from existing rule.
            const isUnique = allExistingRules.indexOf(JSON.stringify(rule)) < 0;                                    // Check JSON array for new rule.

            // Add new rule to existing rule.
            if (isUnique) rules[watchTarget][setTarget] = { all: existingRule.all ? existingRule.all.concat(rule) : [existingRule, rule] };
        }
        return rules;
    }

    /* ----- Reducers ----- */

    /**
     * [Pure]
     * @param arr 
     * @param item 
     */
    itemReducer(arr: Array<FormItem>, item: FormItem): Array<FormItem> {
        if (item) arr.push(item);
        if (!item.items) return arr;
        return item.items.reduce(this.itemReducer, arr);
    }

    /**
     * [Pure]
     * @param initial 
     * @param item 
     */
    requiredReducer(initial: boolean, item: FormItem): boolean {
        if (!initial) return false;
        let { visible, required, valid, items } = item;
        if (!visible) return true;
        if (required && !valid) return false;
        if (visible && items && items.length) return items.reduce(this.requiredReducer, true);
        return true;
    }

    /**
     * [Pure]
     * @param ruleObj
     * @param item
     */
    ruleReducer(ruleObj: any, item: FormItem): any {
        // If no rules return rule object.
        if (!item.rules) return ruleObj;

        // Copy setTarget values from item.rules to rule object.
        Object.keys(item.rules).forEach(watchTarget => {
            const ruleset = item.rules[watchTarget];
            Object.keys(ruleset).forEach(setTarget => {
                this.addRule(ruleObj, watchTarget, setTarget, ruleset[setTarget]);
            });
        });

        // Recurse on children.
        if (item.items && item.items.length) return item.items.reduce(this.ruleReducer, ruleObj);

        // Return rule object.
        return ruleObj;
    }

    /**
     * [Pure]
     * @param initial 
     * @param item 
     */
    sumReducer(initial: number, item: FormItem): number {
        return initial + (parseInt(item.value) || 0);
    }

    /**
     * [?]
     * @param updates
     * @param watchTarget - Item id to watch.
     */
    updateReducer(updates: any, watchTarget: string) {
        // Get rules.
        if (watchTarget.indexOf(".") > -1) watchTarget = watchTarget.split(".")[0];
        let ruleset = this.get("rules", null, {})[watchTarget];
        if (!ruleset) return updates;

        return Object.keys(ruleset).reduce((updates: any, setTarget: string) => {
            // Get existing value.
            let prev = this.get(setTarget, updates);
            if(prev === undefined) prev = this.get(setTarget);

            // Get rule engine result.
            const result: any = this.ruleEngine(ruleset[setTarget]);

            // If result is undefined or unchanged, return.
            if (result === undefined || result === prev) {
                return updates;
            }

            // Set value in updates.
            const setTargetId = setTarget.split(".")[0];
            if (setTarget.indexOf(".") < 0) updates[setTarget] = result;
            else {
                if (updates[setTargetId] === undefined) updates[setTargetId] = this.get(setTargetId);
                this.deepSet(updates, setTarget, result);
            }

            // Recurse new update.
            return this.updateReducer(updates, setTargetId);
        }, updates);
    }

    /* ----- Form Item Methods ----- */

    initItem(formItem: FormItem, config?: any): FormItem {
        // Alias config props.
        const { parent, append, prepend } = (config || { parent: {}, append: false, prepend: false });

        // Get new items.
        if (!formItem || !formItem.items) return (console.log("Item could not be initialized", formItem), formItem);
        const newItems = this.itemReducer([], formItem);

        // Update items with default values and rules.
        const defaults = {
            visible: true,
            disabled: false,
            valid: false,
            placeholder: "",
            rules: {}
        };
        newItems.forEach(item => {
            // Copy default values without overwriting.
            this.copy(item, defaults, { notExisting: true });

            // Alias item properties.
            const { type, rules, itemId, parentItemId, items } = item;

            // Default rules.
            const hasValue = { compare: [`#${itemId}.value`, "!!"] };
            const parentHasValue = { compare: [`#${parentItemId}.value`, "==", itemId] };

            // Validation.
            if (item.required && ["aggregate", "none", "submit", "checkboxGroup"].indexOf(type) < 0) {
                this.addRule(rules, itemId, `${itemId}.valid`, hasValue);
            }
            
            // Modal visibility.
            if (type === "modal") this.addRule(rules, itemId, `${itemId}.visible`, hasValue);

            // Checkbox group validation (currently only min selection 1).
            if (type === "checkboxGroup") {
                const minSelection = { any: items.map(child => ({ compare: [`#${child.itemId}.value`, "==", true] })) };
                items.forEach(child => this.addRule(rules, child.itemId, `${itemId}.valid`, minSelection));
            }

            // Radio validation & child visibility.
            if (items && items.length && type === "radio") {
                this.addRule(rules, parentItemId, `${itemId}.valid`, parentHasValue);
                items.forEach(child => this.addRule(rules, parentItemId, `${child.itemId}.visible`, parentHasValue));
            }

            // Input child visibility.
            if (items && items.length && ["text", "textarea", "date", "email", "number", "checkbox"].indexOf(type) > -1) {
                items.forEach(child => {
                    if (child.className.indexOf("check-na") > -1) return;
                    this.addRule(rules, itemId, `${child.itemId}.visible`, hasValue);
                });
            }
        });

        // Add rules to global rules.
        const rules = newItems.reduce(this.ruleReducer, this.store.rules || {});
        this.set({ rules });

        // Batch updates.
        const updates = newItems.reduce((obj: any, item: FormItem) => (!item || !item.itemId) ? obj : (obj[item.itemId] = item, obj), {});  // Add new form items.
        Object.keys(rules).forEach(id => { if (updates[id] === undefined) updates[id] = this.get(id); });                                   // Add all watched values.
        this.set(updates, { batch: true });

        // Add item to parent.
        if (append) parent.items.push(formItem);
        if (prepend) parent.items.unshift(formItem);

        return formItem;
    }

}
